import { useState, useMemo, useRef } from 'react';
import { ProyectoMock } from '../../../mocks/proyectos';
import { ActiveTab, RespuestaAutor, InformeAvance, RetroalimentacionComite } from './seguimiento/types';
import TabConDecision from './seguimiento/TabConDecision';
import TabInformeAvance from './seguimiento/TabInformeAvance';
import TabReportes from './seguimiento/TabReportes';

interface Props {
  proyectos: ProyectoMock[];
  isAdmin?: boolean;
}

export default function SeguimientoReportes({ proyectos }: Props) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('proyectos-decision');
  const [respuestasEnviadas, setRespuestasEnviadas] = useState<RespuestaAutor[]>([]);
  const [informesAvance, setInformesAvance] = useState<InformeAvance[]>([]);
  const [retroalimentaciones, setRetroalimentaciones] = useState<RetroalimentacionComite[]>([]);

  const lastAvanceMailRef = useRef<{ subject: string; body: string; email: string } | null>(null);

  const proyectosConDecision = useMemo(() =>
    proyectos.filter(p => p.decision && p.decision !== '—' && p.fechaDecision && p.fechaDecision !== '—'),
    [proyectos]
  );

  const respuestasPorCodigo = useMemo(() => {
    const map: Record<string, RespuestaAutor[]> = {};
    respuestasEnviadas.forEach(r => {
      if (!map[r.codigo]) map[r.codigo] = [];
      map[r.codigo].push(r);
    });
    return map;
  }, [respuestasEnviadas]);

  const retroPorCodigo = useMemo(() => {
    const map: Record<string, RetroalimentacionComite[]> = {};
    retroalimentaciones.forEach(r => {
      if (!map[r.codigo]) map[r.codigo] = [];
      map[r.codigo].push(r);
    });
    return map;
  }, [retroalimentaciones]);

  const handleNuevaRespuesta = (resp: RespuestaAutor) => {
    setRespuestasEnviadas(prev => [...prev, resp]);
  };

  const handleNuevoInforme = (inf: InformeAvance) => {
    setInformesAvance(prev => [...prev, inf]);
  };

  const handleNuevaRetro = (retro: RetroalimentacionComite) => {
    setRetroalimentaciones(prev => [...prev, retro]);
  };

  const TABS: { key: ActiveTab; label: string; icon: string }[] = [
    { key: 'proyectos-decision', label: 'Con decisión', icon: 'ri-award-line' },
    { key: 'form', label: 'Informe de avance', icon: 'ri-file-add-line' },
    { key: 'reportes', label: 'Reportes / Respuestas', icon: 'ri-list-check' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-md">
            <i className="ri-bar-chart-line text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Seguimiento y Reportes</h2>
        </div>
        <p className="text-slate-500 text-sm ml-11">
          Visualice el estado de cada proyecto, revise los informes de avance enviados por los investigadores y emita retroalimentación por rondas.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-6 flex-wrap">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`whitespace-nowrap px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer ${
              activeTab === tab.key ? 'bg-white text-teal-700' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center"><i className={tab.icon} /></span>
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'proyectos-decision' && (
        <TabConDecision
          proyectosConDecision={proyectosConDecision}
          respuestasEnviadas={respuestasEnviadas}
          respuestasPorCodigo={respuestasPorCodigo}
        />
      )}

      {activeTab === 'form' && (
        <TabInformeAvance
          proyectos={proyectos}
          respuestasEnviadas={respuestasEnviadas}
          respuestasPorCodigo={respuestasPorCodigo}
          informesAvance={informesAvance}
          retroalimentaciones={retroalimentaciones}
          retroPorCodigo={retroPorCodigo}
          onInformeEnviado={handleNuevoInforme}
          onNuevaRetro={handleNuevaRetro}
          onNuevaRespuesta={handleNuevaRespuesta}
          lastMailRef={lastAvanceMailRef}
        />
      )}

      {activeTab === 'reportes' && (
        <TabReportes
          proyectos={proyectos}
          proyectosConDecision={proyectosConDecision}
          respuestasEnviadas={respuestasEnviadas}
          respuestasPorCodigo={respuestasPorCodigo}
          informesAvance={informesAvance}
          retroalimentaciones={retroalimentaciones}
          retroPorCodigo={retroPorCodigo}
        />
      )}
    </div>
  );
}
